import { ClientPostTypeEnum } from '@DigitaleDoerfer/digitale-doerfer-api';
import { useTranslation } from 'react-i18next';

import type { FeatureContextValues } from '@/features/feature-management/provider/useFeature';
import { useFeature } from '@/features/feature-management/provider/useFeature';
import { FeaturesEnum } from '@/features/feature-management/types/features';
import type {
  PostFeatureSetting,
  PostFeature
} from '@/features/feature-management/types/postTypeFeature';
import { PostFeedType } from '@/features/posts/types/postFeed';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';

const defaultMayNumberCharsPerPost = 360;

export const useGetPostTypeFeature = (
  postType: PostFeedType | ClientPostTypeEnum
): PostFeatureSetting => {
  const { getFeature } = useFeature();
  const { t } = useTranslation(LocalizationNameSpace.POSTS, {
    keyPrefix: 'types'
  });

  switch (postType) {
    case PostFeedType.ALL:
    case PostFeedType.GOSSIP: {
      return getPostTypeFeature(
        getFeature,
        t(PostFeedType.GOSSIP),
        FeaturesEnum.Gossip,
        20
      );
    }
    case PostFeedType.NEWS_ITEM: {
      return getPostTypeFeature(
        getFeature,
        t(PostFeedType.NEWS_ITEM),
        FeaturesEnum.NewsItem,
        10
      );
    }
    case PostFeedType.HAPPENING: {
      return getPostTypeFeature(
        getFeature,
        t(PostFeedType.HAPPENING),
        FeaturesEnum.Happening,
        30
      );
    }
    case PostFeedType.MARKET: {
      const seekingFeature = getPostTypeFeature(
        getFeature,
        t(ClientPostTypeEnum.Seeking),
        FeaturesEnum.Seeking,
        44
      );
      const offerFeature = getPostTypeFeature(
        getFeature,
        t(ClientPostTypeEnum.Seeking),
        FeaturesEnum.Seeking,
        44
      );
      return {
        featureIdentifier: offerFeature.featureIdentifier,
        id: offerFeature.id,
        channelName: t(PostFeedType.MARKET),
        itemName: t(PostFeedType.MARKET),
        featureValues: offerFeature.featureValues,
        enabled: offerFeature.enabled || seekingFeature.enabled,
        localEnabled: offerFeature.localEnabled || seekingFeature.localEnabled,
        remoteEnabled:
          offerFeature.remoteEnabled || seekingFeature.remoteEnabled,
        maxNumberCharsPerPost: Math.min(
          offerFeature.maxNumberCharsPerPost,
          seekingFeature.maxNumberCharsPerPost
        ),
        enabledPersonConfigurable:
          offerFeature.enabledPersonConfigurable ||
          seekingFeature.enabledPersonConfigurable,
        valuesPersonConfigurable:
          offerFeature.valuesPersonConfigurable ||
          seekingFeature.valuesPersonConfigurable,
        orderValue: Math.floor(
          (offerFeature.orderValue + seekingFeature.orderValue) / 2
        )
      };
    }
    case ClientPostTypeEnum.Seeking: {
      return getPostTypeFeature(
        getFeature,
        t(ClientPostTypeEnum.Seeking),
        FeaturesEnum.Seeking,
        44
      );
    }
    case ClientPostTypeEnum.Offer: {
      return getPostTypeFeature(
        getFeature,
        t(ClientPostTypeEnum.Offer),
        FeaturesEnum.Offer,
        42
      );
    }
    case PostFeedType.SUGGESTION: {
      return getPostTypeFeature(
        getFeature,
        t(PostFeedType.SUGGESTION),
        FeaturesEnum.Suggestion,
        50
      );
    }
    case PostFeedType.SPECIAL_POST: {
      return getPostTypeFeature(
        getFeature,
        t(PostFeedType.SPECIAL_POST),
        FeaturesEnum.SpecialPost,
        60
      );
    }
    case PostFeedType.EMERGENCY: {
      return getPostTypeFeature(
        getFeature,
        t(PostFeedType.EMERGENCY),
        FeaturesEnum.Emergency,
        15
      );
    }
    case PostFeedType.CIVIL_PROTECTION: {
      return getPostTypeFeature(
        getFeature,
        t(PostFeedType.CIVIL_PROTECTION),
        FeaturesEnum.CivilProtection,
        16
      );
    }
  }
};

const getPostTypeFeature = (
  getFeature: FeatureContextValues['getFeature'],
  defaultName: string,
  featureIdentifier: FeaturesEnum,
  defaultSort: number
): PostFeatureSetting => {
  const featureSetting = getFeature<PostFeature>(featureIdentifier) ?? {
    id: featureIdentifier,
    featureIdentifier
  };
  const featureValues = featureSetting?.featureValues;

  return {
    ...featureSetting,
    channelName: featureValues?.['channelName'] ?? defaultName,
    itemName: featureValues?.['itemName'] ?? defaultName,
    maxNumberCharsPerPost:
      featureValues?.['maxNumberCharsPerPost'] ?? defaultMayNumberCharsPerPost,
    orderValue: featureValues?.['orderValue'] ?? defaultSort
  };
};
